// Which transport the typed API client uses, decided per request (BL-2343, ADR 066 — Offline
// Mode § 3 and § 6.1 limit 4): the network, unless this build runs offline mode and the page is
// an offline document or the phone app is in offline mode. Online, the store is never read.
import { adminRegistry } from '@auctogon/core-admin/api-registry';
import {
  isAuthPagePath,
  isOfflineDocumentPath,
  isOfflineModeBuild,
} from '@auctogon/admin-isomorphic/isomorphic/offline/offline-routes';

import { startsInManualOffline } from '../../components/offline/manual-offline-flag';
import {
  getNativeDeviceGateway,
  NativeCapabilityUnavailableError,
  type OfflineStore,
} from '../../gateways/native-device';
import { createOfflineTransport } from './create-offline-transport';
import { offlineHandlers } from './offline-handlers';

/**
 * The offline mode, as `OfflineModeProvider` last reported it (both offline kinds are true), or
 * null until it first reports. It reports only once native detection settles, after the page's
 * first effects have already read (BUG-2053).
 */
let modeOffline: boolean | null = null;

/**
 * Record the phone app's offline mode (BL-2342's provider calls this on every change).
 *
 * @param offline - true in manual or automatic offline mode inside the native app.
 * @sideEffect Changes which transport later typed-client calls use.
 */
export function setOfflineModeActive(offline: boolean): void {
  modeOffline = offline;
}

/**
 * Whether typed-client calls are answered from the device store right now.
 *
 * @returns true in an offline-mode build when the page is an offline document (a connection that
 *   failed) or the app is in offline mode (before the provider reports, when the app was left in
 *   manual offline mode); always false on a sign-in page (BUG-2197) and in a build outside
 *   `OFFLINE_MODE_BUILD_ENVS`.
 */
export function isOfflineRoutingActive(): boolean {
  if (!isOfflineModeBuild(process.env.NEXT_PUBLIC_AUCTOGON_ENV)) return false;
  // The sign-in pages ask the server whether a session exists. The device keeps its stored session
  // past sign-out and expiry, so answering from it there reads a signed-out user as signed in and
  // strands the page on its spinner (BUG-2197).
  if (typeof window !== 'undefined' && isAuthPagePath(window.location.pathname)) return false;
  const onOfflineDocument = typeof window !== 'undefined' && isOfflineDocumentPath(window.location.pathname);
  // Before the provider reports, the stored manual flag decides (ADR 066 — Offline Mode § 5: it
  // is per device and survives a relaunch), so a relaunch in manual offline mode reads nothing
  // from the server (BUG-2053).
  return onOfflineDocument || (modeOffline ?? startsInManualOffline());
}

/**
 * The active scope's store, or null when the device has none (never signed in, signed out, or a
 * browser). Any other failure rejects, so the request fails as a network failure would and the
 * cause is logged by the caller's error path.
 *
 * @returns the store, or null.
 * @sideEffect App: opens IndexedDB.
 */
async function openActiveStore(): Promise<OfflineStore | null> {
  const gateway = await getNativeDeviceGateway();
  try {
    const scope = await gateway.readActiveOfflineScope();
    return scope ? await gateway.openOfflineStore(scope) : null;
  } catch (err) {
    if (err instanceof NativeCapabilityUnavailableError) return null;
    throw new Error('Could not open the offline device store', { cause: err });
  }
}

/** Built on the first offline request, so an online session never compiles the matcher. */
let offlineTransport: typeof fetch | null = null;

function getOfflineTransport(): typeof fetch {
  offlineTransport ??= createOfflineTransport({
    registry: adminRegistry,
    handlers: offlineHandlers,
    openStore: openActiveStore,
  });
  return offlineTransport;
}

/**
 * The typed client's `fetch`: the offline transport while offline routing is active, the global
 * `fetch` (resolved per call, so test spies hold) otherwise.
 *
 * @param input - the request.
 * @param init - its options.
 * @returns the response.
 * @sideEffect Online: a network request. Offline: a device-store read.
 */
export const apiFetch: typeof fetch = (input, init) =>
  isOfflineRoutingActive() ? getOfflineTransport()(input, init) : globalThis.fetch(input, init);

/** Test seam: start a new page load (the provider has reported nothing yet). */
export function __resetOfflineRoutingForTest(): void {
  modeOffline = null;
}
